/**
 * Status pill — a colored dot plus a label, on a tinted round background.
 *
 * Semantic status picks the colors; the label is always passed in, since this app is PT-BR and
 * the wording differs per screen ("Ativa" for a page, "Entregue" for a signature).
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX, ReactNode } from 'react';

/** Semantic status, mapped to the token pair that colors the pill. */
export type Status = 'success' | 'pending' | 'danger' | 'neutral' | 'info';

const STATUS_STYLE: Record<Status, { color: string; background: string }> = {
  success: { color: 'var(--color-success)', background: 'var(--color-success-subtle)' },
  pending: { color: 'var(--gl-yellow-dark)', background: 'var(--color-warning-subtle)' },
  danger: { color: 'var(--color-danger)', background: 'var(--color-danger-subtle)' },
  neutral: { color: 'var(--color-text-muted)', background: 'var(--color-surface-sunken)' },
  info: { color: 'var(--color-info)', background: 'var(--color-info-subtle)' },
};

export interface StatusTagProps {
  status: Status;
  children: ReactNode;
  /** Leading dot. On by default. */
  dot?: boolean;
  size?: 'sm' | 'md';
  id?: string;
}

export const StatusTag = ({
  status,
  children,
  dot = true,
  size = 'md',
  id,
}: StatusTagProps): JSX.Element => {
  const { color, background } = STATUS_STYLE[status];

  return (
    <span
      id={id}
      className={css`
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: ${size === 'sm' ? '2px 8px' : '4px 10px'};
        border-radius: var(--radius-round);
        background: ${background};
        color: ${color};
        font-size: ${size === 'sm' ? 'var(--font-size-2xs)' : 'var(--font-size-xs)'};
        font-weight: var(--font-weight-semibold);
        line-height: 1.4;
        white-space: nowrap;
      `}
    >
      {dot ? (
        <span
          className={css`
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: ${color};
            flex: 0 0 auto;
          `}
        />
      ) : null}
      {children}
    </span>
  );
};
